import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy, promedio, conComa } from '../utils/formato';
import { lecturaEnTinas, enQuesos, kilosPorTina } from '../utils/tina';
import { mezcla, topeDeMezcla, nombreDelTipo, BUFALA } from '../utils/leche';
import { cuajoSugerido, CM_DE_CUAJO, LITROS_POR_MEDIDA } from '../utils/insumo';
import { lecturaDeRuta, compararRutas } from '../utils/ruta';
import { sospechosos, fincasDelDia, esSospechoso } from '../utils/sospechosos';

// Cerrar una cuajada.
//
// Es la pantalla más importante del programa. De aquí salen el lote, el
// rendimiento y el costo del kilo, que son las tres cosas que deciden si el
// negocio gana.
//
// El rendimiento se ve MIENTRAS se escribe, no después de guardar. Un informe
// mensual que diga que rindió mal llega cuando el mes ya se perdió; verlo al
// cerrar la cuajada permite ir a mirar la leche o el proceso el mismo día.
function Produccion() {
  const [productos, setProductos] = useState([]);
  const [cuajadas, setCuajadas] = useState([]);
  // Cuántos litros de búfala llevó la tina. El resto es de vaca.
  const [litrosBufala, setLitrosBufala] = useState('');
  const [precioDia, setPrecioDia] = useState(null);

  const [productoId, setProductoId] = useState('');
  const [litros, setLitros] = useState('');
  const [kilos, setKilos] = useState('');
  const [piezas, setPiezas] = useState('');
  const [dia, setDia] = useState(hoy());
  const [costoInsumos, setCostoInsumos] = useState('');
  // Qué insumos llevó la cuajada: { idDelInsumo: cantidad }. Si se anotan, el
  // costo se calcula solo con el promedio ponderado de lo comprado y deja de
  // teclearse a ojo.
  const [insumos, setInsumos] = useState([]);
  const [recibos, setRecibos] = useState([]);
  const [centros, setRutas] = useState([]);
  const [centroId, setCentroId] = useState('');
  // De cuáles fincas es la leche de esa ruta ese día. No se guarda: se deduce
  // de los recibos, que ya lo saben. Guardarlo sería una segunda versión de la
  // misma lista, y dos versiones terminan diciendo cosas distintas.
  const [fincasDeLaRuta, setFincasDeLaRuta] = useState(null);
  const [centroNuevo, setCentroNuevo] = useState('');
  // Cuando la cuajada es una PRUEBA: la leche de una sola finca, aislada para
  // medirla. Vacío = la mezcla del día, que es lo normal.
  const [fincaSola, setFincaSola] = useState('');
  const [fincas, setFincas] = useState([]);
  // El periodo de la comparación entre centros. Por defecto el mes, que es
  // como se mira un negocio; con «todo» se ve la historia completa.
  const [periodo, setPeriodo] = useState('mes');
  const [porCentro, setPorCentro] = useState(null);
  const [gastoInsumos, setGastoInsumos] = useState([]);
  const [usados, setUsados] = useState({});
  const [costoManoObra, setCostoManoObra] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/productos').then(r => setProductos(r.data || [])).catch(() => {});
    axios.get('/api/cuajadas').then(r => setCuajadas(r.data || [])).catch(() => {});
    axios.get('/api/insumos/resumen').then(r => setInsumos(r.data || [])).catch(() => {});
    // Los recibos son los que dicen de qué fincas salió la leche de cada día.
    // No se le pide al usuario que lo anote: ya está anotado.
    axios.get('/api/recibos-leche').then(r => setRecibos(r.data || [])).catch(() => {});
    axios.get('/api/fincas').then(r => setFincas(r.data || [])).catch(() => {});
    axios.get('/api/centros').then(r => {
      const lista = r.data || [];
      setRutas(lista);
      // Con UN solo centro no se pregunta —la respuesta es una sola— pero SÍ se
      // anota. Antes no se preguntaba y tampoco se anotaba: la cuajada quedaba
      // como «sin centro», y quien se tomó el trabajo de crear la ruta no
      // entendía por qué.
      if (lista.length === 1) setCentroId(String(lista[0].id));
    }).catch(() => {});
  }, []);

  // La comparación se pide al servidor con el rango, en vez de calcularla con
  // las cuajadas que ya están cargadas: esa lista trae solo las últimas 300, y
  // un negocio con dos años de producción vería una comparación recortada sin
  // que nada se lo dijera.
  useEffect(() => {
    const hoyTxt = hoy();
    let desde = '1900-01-01';
    if (periodo !== 'todo') {
      const d = new Date();
      if (periodo === 'semana') d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      else d.setDate(1);
      desde = d.toISOString().split('T')[0];
    }
    axios.get(`/api/centros/rendimiento?desde=${desde}&hasta=${hoyTxt}`)
      .then(r => setPorCentro(r.data))
      .catch(() => setPorCentro(null));
    axios.get(`/api/centros/insumos?desde=${desde}&hasta=${hoyTxt}`)
      .then(r => setGastoInsumos(r.data || []))
      .catch(() => setGastoInsumos([]));
  }, [periodo, centros]);

  useEffect(cargar, [cargar]);

  // El precio de la leche lo pone el programa: es el promedio ponderado de lo
  // que se recibió ese día. Nadie lo teclea, porque nadie lo sabría de memoria
  // cuando tres fincas entregaron a precios distintos.
  useEffect(() => {
    let vivo = true;
    // Si es una prueba, el precio es el de ESA finca. El promedio del día
    // mentiría: si ella puso 500 litros y otra puso 10.000, el promedio es casi
    // el de la otra, y la prueba quedaría costeada con leche que no usó.
    const q = fincaSola ? `&finca_id=${fincaSola}` : '';
    // Y del tipo de leche: costear una cuajada de búfala con el promedio del
    // día —que incluye la de vaca, más barata— haría que el queso de búfala
    // saliera más barato de lo que costó, y su margen sería el más bonito del
    // informe justo por estar mal calculado.
    // Llegan los DOS precios del día. La tina lleva de las dos leches en
    // proporciones que cambian, y con un promedio único la que llevó más
    // búfala se costearía igual que la que llevó poca.
    axios.get(`/api/cuajadas/precio-leche?fecha=${dia}${q}`)
      .then(r => { if (vivo) setPrecioDia(r.data); })
      .catch(() => { if (vivo) setPrecioDia(null); });
    return () => { vivo = false; };
  }, [dia, fincaSola]);

  const producto = productos.find(p => String(p.id) === String(productoId));
  // El suero se hace del suero dulce que queda al cuajar, no de leche. Sale de
  // la ficha del producto y no de una casilla en esta pantalla: una casilla que
  // hay que marcar cada vez algún día se marca mal, y una producción de suero
  // contada como leche daña el cuadre del día.
  const haciendoSuero = !!producto && producto.materia === 'suero';

  // Se pide al escoger la ruta o al cambiar el día. El suero no viene de
  // ninguna ruta, así que ahí no se pregunta nada.
  useEffect(() => {
    if (!centroId || haciendoSuero) { setFincasDeLaRuta(null); return; }
    let vivo = true;
    axios.get(`/api/centros/${centroId}/fincas-del-dia`, { params: { fecha: dia } })
      .then(r => { if (vivo) setFincasDeLaRuta(r.data || null); })
      .catch(() => { if (vivo) setFincasDeLaRuta(null); });
    return () => { vivo = false; };
  }, [centroId, dia, haciendoSuero]);

  const nLitros = parseFloat(litros) || 0;
  const nKilos = parseFloat(kilos) || 0;
  const rendimiento = nKilos > 0 ? nLitros / nKilos : null;
  // LA MEZCLA. Se suman los kilos que debe dar cada leche, no sus razones:
  // promediar los litros-por-kilo da un número más permisivo y dejaría pasar
  // las cuajadas malas. La cuenta vive en utils/leche.js, con sus pruebas.
  const nBufala = Math.min(parseFloat(litrosBufala) || 0, nLitros);
  const nVaca = Math.max(0, nLitros - nBufala);
  const laMezcla = mezcla({ litrosVaca: nVaca, litrosBufala: nBufala }, producto);
  const esperado = laMezcla ? laMezcla.esperado : null;
  const topeMezcla = topeDeMezcla({ litrosVaca: nVaca, litrosBufala: nBufala }, producto);

  // Más de un 5% peor que lo normal ya es señal. Por debajo de eso es el ruido
  // de siempre entre una cuajada y otra, y avisar por todo enseña a ignorar.
  const rindeMal = rendimiento && esperado &&
                   rendimiento > (topeMezcla || esperado * 1.05);
  const rindeBien = rendimiento && esperado && rendimiento <= esperado;

  // Lo que cuestan los insumos anotados, al promedio ponderado de compra. Si
  // no se anota ninguno, vale lo que se escriba a mano en la casilla.
  const costoDeLosInsumos = insumos.reduce((s, i) => {
    const cant = parseFloat(usados[i.id]) || 0;
    return s + cant * (i.costo_unitario || 0);
  }, 0);
  const hayAnotados = costoDeLosInsumos > 0;

  // Cada leche a su precio. Si no hubo búfala ese día se usa el de la vaca:
  // vale más costear con lo que hay que dejar la cuajada sin costo.
  const pVaca = precioDia?.vaca ?? null;
  const pBufala = precioDia?.bufala ?? pVaca;
  const costoLeche = pVaca ? Math.round(nVaca * pVaca + nBufala * (pBufala || pVaca)) : 0;
  const costoTotal = costoLeche + (hayAnotados ? costoDeLosInsumos : (parseFloat(costoInsumos) || 0)) + (parseFloat(costoManoObra) || 0);
  const costoKilo = nKilos > 0 ? costoTotal / nKilos : null;

  const guardar = async (e) => {
    e.preventDefault();
    setAviso(null);
    setGuardando(true);
    try {
      const { data } = await axios.post('/api/cuajadas', {
        producto_id: parseInt(productoId),
        litros_vaca: nVaca,
        litros_bufala: nBufala,
        centro_id: centroId ? parseInt(centroId) : null,
        finca_id: fincaSola ? parseInt(fincaSola) : null,
        fecha: dia,
        litros: nLitros,
        kilos: nKilos,
        piezas: piezas ? parseInt(piezas) : null,
        costo_insumos: hayAnotados ? Math.round(costoDeLosInsumos) : (parseFloat(costoInsumos) || 0),
        costo_mano_obra: parseFloat(costoManoObra) || 0
      });
      setAviso({
        tipo: data.alerta ? 'ojo' : 'bueno',
        texto: data.alerta
          ? `Lote ${data.lote} guardado. ${data.alerta}`
          : `Lote ${data.lote} guardado: ${data.rendimiento} L/kg a ${plata(data.costo_kilo)} el kilo. Vence el ${fecha(data.vence)}.`
      });
      // Los insumos que llevó, para que salgan de la bodega. Va después de
      // crear la cuajada porque necesita su id, y si esto falla la cuajada ya
      // quedó guardada: vale más una producción registrada sin el detalle de
      // insumos que perder el lote entero por un tropiezo aquí.
      if (hayAnotados) {
        try {
          await axios.post(`/api/cuajadas/${data.id}/insumos`, {
            insumos: insumos
              .filter(i => (parseFloat(usados[i.id]) || 0) > 0)
              .map(i => ({ insumo_id: i.id, cantidad: parseFloat(usados[i.id]) })),
          });
        } catch {
          setAviso({
            tipo: 'ojo',
            texto: `Lote ${data.lote} guardado, pero no se pudieron descontar los ` +
                   'insumos de la bodega. Anótalos a mano en la pantalla de Insumos.',
          });
        }
      }

      setLitros(''); setKilos(''); setPiezas(''); setFincaSola('');
      setCostoInsumos(''); setCostoManoObra(''); setUsados({});
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const borrar = async (c) => {
    if (!window.confirm(
      `¿Borrar el lote ${c.lote}?\n\n${peso(c.kilos, 'kg')} de ${c.producto} saldrán del inventario.`
    )) return;
    try {
      await axios.delete(`/api/cuajadas/${c.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  return (
    <>
      <div className="encabezado">
        <h1>Producción</h1>
        <p>Cada cuajada es un lote: entran litros, salen kilos</p>
      </div>

      <div className="bloque">
        <h2>🧀 Cerrar una cuajada</h2>
        <form className="cuerpo" onSubmit={guardar}>
          {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

          <div className="campos">
            <label className="campo">
              <span>Variedad *</span>
              <select value={productoId} onChange={e => setProductoId(e.target.value)} required>
                <option value="">— Elegir variedad —</option>
                {productos.map(p => (
                  <option key={p.id} value={p.id}>
                    {/* El suero no rinde «litros por kilo»: rinde litros por
                        litro, y se dice como lo dice el quesero — «de 200
                        salen 15». Con el rótulo del queso, el número de al
                        lado no significa nada y estorba. */}
                    {p.nombre}{p.rendimiento_esperado
                      ? (p.materia === 'suero'
                          ? ` · de 200 L salen ${conComa(Math.round((200 / p.rendimiento_esperado) * 10) / 10)}`
                          : ` · normal ${conComa(p.rendimiento_esperado)} L/kg`)
                      : ''}
                  </option>
                ))}
              </select>
            </label>

            {/* Solo aparece si hay más de una ruta. Quien tiene uno solo no
                tiene por qué contestar en cuál lo hizo: es una pregunta con
                una sola respuesta posible, y esas son las que sobran.

                Y NO aparece para el suero: el suero no viene de una ruta, sale
                del queso que ya se hizo. Preguntarlo dejaría rutas cargadas con
                litros que ninguna finca entregó. */}
            {centros.length > 1 && !haciendoSuero && (
              <label className="campo">
                <span>¿En cuál ruta? *</span>
                <select value={centroId} onChange={e => setCentroId(e.target.value)} required>
                  <option value="">— Elegir —</option>
                  {centros.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
            )}

            {/* Los rótulos cambian con lo que se esté haciendo. El suero no
                entra en litros de LECHE ni sale en KILOS: entra suero dulce y
                salen litros. Con los rótulos del queso, el quesero teclea
                creyendo una cosa y el programa guarda otra. */}
            {/* DE CUÁLES FINCAS ES ESTA LECHE.
                
                Se deduce de los recibos: son las que entregaron a esa ruta ese
                día. Sirve para dos cosas — confirmar que se escogió la ruta
                correcta antes de teclear nada, y dejar el lote trazable hasta
                las fincas que lo hicieron.
                
                Y dice el límite en voz alta: si esa leche se reparte en dos
                tinas, no se puede saber cuál finca fue a cuál. Está revuelta
                en el tanque, y un número por finca y por tina sería inventado. */}
            {fincasDeLaRuta && (
              <div style={{ gridColumn: '1 / -1' }}>
                {fincasDeLaRuta.fincas.length === 0 ? (
                  <div className="aviso ojo" style={{ margin: 0 }}>
                    Ninguna finca entregó a esa ruta el {fecha(dia)}. Revise la
                    ruta o la fecha antes de guardar.
                  </div>
                ) : (
                  <div className="aviso" style={{ margin: 0 }}>
                    <strong>
                      Esa ruta recibió {peso(fincasDeLaRuta.litros, 'L')} el{' '}
                      {fecha(dia)}, de {fincasDeLaRuta.fincas.length}{' '}
                      {fincasDeLaRuta.fincas.length === 1 ? 'finca' : 'fincas'}
                    </strong>
                    {fincasDeLaRuta.bufala > 0 &&
                      <> — {peso(fincasDeLaRuta.bufala, 'L')} de búfala</>}
                    {/* Un toque para no teclear los litros a mano cuando la
                        tina lleva toda la leche de la ruta.
                        
                        PONE LO QUE QUEDA PARA LA TINA, no todo lo recibido.
                        
                        La leche que se vende cruda —la que se lleva Juan
                        Pablo— no pasa por ninguna tina, y el botón la metía.
                        El 2 de septiembre entraron 6.941 litros, se cuajaron
                        los 6.941 Y ADEMÁS se vendieron 366 crudos: 366 litros
                        contados dos veces. Cayeron en la cuajada de RETÉN y esa
                        ruta apareció rindiendo 5,76 kilos por tina cuando
                        rendía 6,27 — la ruta más grande del negocio,
                        viéndose como la peor durante días. */}
                    {!litros && (
                      <button type="button" className="boton chico suave"
                              style={{ marginLeft: 10 }}
                              onClick={() => {
                                setLitros(String(fincasDeLaRuta.para_la_tina
                                                 ?? fincasDeLaRuta.litros));
                                if (fincasDeLaRuta.bufala > 0)
                                  setLitrosBufala(String(fincasDeLaRuta.bufala));
                              }}>
                        Usar esos litros
                      </button>
                    )}
                    {/* Y se dice la resta, siempre. Un botón que pone una cifra
                        distinta de la que se acaba de leer arriba, sin
                        explicar por qué, se lee como que el programa se
                        equivocó — y quien lo use va a corregirlo a mano. */}
                    {fincasDeLaRuta.vendida_cruda > 0 && (
                      <div className="aviso ojo" style={{ marginTop: 10 }}>
                        Ese día se vendieron <strong>
                        {peso(fincasDeLaRuta.vendida_cruda, 'L')}</strong> de
                        leche cruda, que no pasan por ninguna tina.{' '}
                        <strong>Van {peso(fincasDeLaRuta.para_la_tina, 'L')}</strong>{' '}
                        a cuajar, no los {peso(fincasDeLaRuta.litros, 'L')} que
                        entraron.
                        <div style={{ marginTop: 6 }}>
                          Se descuenta aquí porque esta es la ruta por donde sale
                          la leche cruda. Si ese día salió por otra, corrija los
                          litros a mano.
                        </div>
                      </div>
                    )}
                    {/* EN LAS DEMÁS RUTAS SE DICE, PERO NO SE RESTA.
                        
                        A LA YERA, que recibió 383 L, se le proponían 12: los
                        371 de leche cruda del día habían salido de RETÉN. Una
                        cifra absurda rellenada sola es peor que no rellenar
                        nada, y con un caso menos extremo se guarda sin que
                        nadie lo note.
                        
                        Se dice igual, porque si ese día la leche cruda SÍ salió
                        de aquí, hay que restarla a mano — y sin el aviso nadie
                        se acordaría. */}
                    {!fincasDeLaRuta.vende_cruda &&
                     fincasDeLaRuta.cruda_del_dia > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12.5,
                                    color: 'var(--tinta-tenue)' }}>
                        Ese día se vendieron{' '}
                        {peso(fincasDeLaRuta.cruda_del_dia, 'L')} de leche cruda,
                        pero <strong>no salen de esta ruta</strong>, así que los{' '}
                        {peso(fincasDeLaRuta.litros, 'L')} de arriba van
                        completos a la tina. Si ese día sí salió de aquí,
                        descuéntelos a mano.
                      </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
                      {fincasDeLaRuta.fincas.map((f, i) => (
                        <span key={i}>
                          {i > 0 && ' · '}
                          {f.nombre} <strong>{peso(f.litros, 'L')}</strong>
                          {f.tipo_leche === 'bufala' && ' 🐃'}
                        </span>
                      ))}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12.5,
                                  color: 'var(--tinta-tenue)' }}>
                      Si esa leche se reparte en varias tinas, no se puede saber
                      cuál finca fue a cuál: está revuelta en el tanque. Lo que
                      queda cierto es que este queso lleva leche de estas fincas.
                    </div>
                  </div>
                )}
              </div>
            )}

            <label className="campo">
              <span>{haciendoSuero ? 'Litros de suero dulce *' : 'Litros de leche *'}</span>
              <input type="number" step="0.1" min="0" className="peso" required
                     value={litros} onChange={e => setLitros(e.target.value)} placeholder="0" />
            </label>

            <label className="campo">
              <span>{haciendoSuero ? 'Litros de suero que salieron *' : 'Kilos de queso *'}</span>
              <input type="number" step="0.01" min="0" className="peso" required
                     value={kilos} onChange={e => setKilos(e.target.value)} placeholder="0" />
            </label>


            {/* Hasta aquí lo obligatorio. Lo de abajo se puede dejar en blanco.

                Sin esta raya, las tres casillas que hay que llenar siempre
                —variedad, litros y kilos— quedaban repartidas entre preguntas
                opcionales, y el quesero no podía ver de un vistazo dónde
                termina lo que le toca. */}
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center',
                          gap: 10, marginTop: 4, color: 'var(--tinta-tenue)',
                          fontSize: 12 }}>
              <span style={{ flex: 1, height: 1, background: 'var(--raya)' }} />
              con esto ya se puede guardar · lo de abajo es opcional
              <span style={{ flex: 1, height: 1, background: 'var(--raya)' }} />
            </div>

            {/* 🐃 CUÁNTA BÚFALA LLEVÓ LA TINA.
                El cliente revuelve las dos leches, así que no es «cuál» sino
                «cuánto de cada una». Se pregunta solo la búfala y el resto es
                vaca: un campo en vez de dos, y no pueden descuadrar.

                Esto cambia dos cosas a la vez: contra qué rendimiento se
                compara —la búfala rinde bastante más— y a cuánto sale el litro,
                porque la búfala cuesta más. */}
            {/* No para el suero: lo que entra es suero dulce, no leche. */}
            {!haciendoSuero && (
            <label className="campo">
              <span>🐃 De esos litros, ¿cuántos de búfala?</span>
              <input type="number" step="0.1" min="0" max={nLitros || undefined}
                     className="peso" value={litrosBufala}
                     placeholder="0 = toda de vaca"
                     onChange={e => setLitrosBufala(e.target.value)} />
            </label>
            )}

            {/* Falta el rendimiento de una leche que SÍ entró en la tina. Se
                dice en vez de callarlo: la cuajada se guarda igual, pero sin
                juzgarla. Media cuenta es peor que ninguna — se presenta con la
                misma confianza que una completa. */}
            {producto && laMezcla && laMezcla.falta && (
              <div className="aviso ojo" style={{ gridColumn: '1 / -1' }}>
                <strong>
                  {producto.nombre} no tiene pactado cuánto rinde con{' '}
                  {laMezcla.falta === BUFALA ? 'búfala' : 'vaca'}.
                </strong>{' '}
                La cuajada se guarda, pero no se le puede decir si rindió bien o
                mal. Se pacta en 🧀 Quesos.
              </div>
            )}

            {/* Qué rendimiento se le va a exigir a ESTA tina, y por qué ese.
                Sin decirlo, el número parece moverse solo de un día a otro. */}
            {laMezcla && laMezcla.esperado && nBufala > 0 && (
              <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                Con <strong>{laMezcla.parteBufala}% de búfala</strong>, esta tina
                debería dar <strong>{laMezcla.kilosEsperados} kilos</strong>
                {' '}— o sea {laMezcla.esperado} L/kg.
              </div>
            )}

            {/* La PRUEBA: cuajar la leche de una finca sola para medirla.
                Es lo que hace el acopiador cuando el rendimiento baja y quiere
                saber cuál finca le está echando agua.

                Va opcional y vacío por defecto: lo normal es la mezcla del día.
                Solo aparece con dos o más fincas — con una sola no hay nada que
                aislar. */}
            {fincas.length > 1 && (
              <label className="campo">
                <span>¿Leche de una sola finca?</span>
                <select value={fincaSola} onChange={e => setFincaSola(e.target.value)}>
                  <option value="">— mezcla del día —</option>
                  {fincas.map(f => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="campo">
              <span>Piezas</span>
              <input type="number" step="1" min="0" className="peso"
                     value={piezas} onChange={e => setPiezas(e.target.value)} placeholder="Opcional" />
            </label>

            <label className="campo">
              <span>Fecha</span>
              <input type="date" value={dia} onChange={e => setDia(e.target.value)} />
            </label>

            <label className="campo">
              <span>Insumos ($)</span>
              <input type="number" step="1" min="0" className="peso"
                     value={hayAnotados ? Math.round(costoDeLosInsumos) : costoInsumos}
                     onChange={e => setCostoInsumos(e.target.value)}
                     disabled={hayAnotados}
                     placeholder="O anótalos abajo" />
              {hayAnotados && (
                <small style={{ color: 'var(--tinta-tenue)' }}>
                  calculado de lo que anotaste
                </small>
              )}
            </label>

            <label className="campo">
              <span>Mano de obra ($)</span>
              <input type="number" step="1" min="0" className="peso"
                     value={costoManoObra} onChange={e => setCostoManoObra(e.target.value)}
                     placeholder="Opcional" />
            </label>
          </div>

          {/* Qué insumos llevó la cuajada.
              Anotarlos hace dos cosas a la vez: descuenta la existencia en
              Insumos y calcula el costo con el promedio ponderado de compra, en
              vez de que alguien lo teclee a ojo. Es opcional: si no se anota
              nada, la casilla de arriba sigue funcionando como antes. */}
          {insumos.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tinta-media)',
                            marginBottom: 8 }}>
                QUÉ INSUMOS LLEVÓ — opcional, pero así el costo sale solo
              </div>
              <div style={{ display: 'grid', gap: 8,
                            gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
                {insumos.map(i => {
                  const cant = parseFloat(usados[i.id]) || 0;
                  const sinExistencia = cant > i.existencia;
                  // EL CUAJO SE SUGIERE: 4 cm por cada 10 litros de leche, que
                  // es la regla de este cliente. Se propone y no se impone —el
                  // botón lo llena, no se llena solo—: si un día echó más, el
                  // inventario tiene que decir lo que se gastó y no lo que
                  // debió gastarse, o deja de servir para pedir a tiempo.
                  const sugerido = i.unidad === 'cm' ? cuajoSugerido(nLitros) : null;
                  return (
                    <div key={i.id} style={{
                      border: `1px solid ${cant > 0 ? 'var(--acento-vivo)' : 'var(--raya)'}`,
                      background: cant > 0 ? 'var(--acento-piso)' : 'var(--tarjeta)',
                      borderRadius: 8, padding: '10px 12px',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                        {i.nombre}
                        <span style={{ fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          {' · '}quedan {peso(i.existencia, i.unidad)}
                        </span>
                      </div>
                      <input
                        type="number" step="0.01" min="0" className="peso"
                        value={usados[i.id] || ''}
                        placeholder={sugerido ? `${sugerido} ${i.unidad}` : `0 ${i.unidad}`}
                        onChange={e => setUsados({ ...usados, [i.id]: e.target.value })}
                      />
                      {sugerido && cant !== sugerido && (
                        <button type="button" className="boton chico suave"
                                style={{ marginTop: 5, width: '100%' }}
                                onClick={() => setUsados({ ...usados, [i.id]: String(sugerido) })}>
                          {CM_DE_CUAJO} cm por cada {LITROS_POR_MEDIDA} L → {sugerido} cm
                        </button>
                      )}
                      {cant > 0 && (
                        <div style={{ fontSize: 12, marginTop: 5,
                                      color: sinExistencia ? 'var(--malo)' : 'var(--tinta-tenue)' }}>
                          {plata(cant * i.costo_unitario)}
                          {sinExistencia && ' · no hay tanto en bodega'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* La cuenta en vivo. Es lo que convierte esta pantalla en una
              herramienta y no en un formulario de captura. */}
          {rendimiento && (
            <div className={'aviso ' + (rindeMal ? 'ojo' : rindeBien ? 'bueno' : '')}
                 style={{ marginTop: 18, marginBottom: 0 }}>
              {/* PRIMERO en tinas, que es como lo piensa quien hace el queso.
                  El L/kg queda debajo, en pequeño: es la cifra exacta y hay que
                  poder verla, pero no es por donde se entiende. */}
              {(() => {
                const t = lecturaEnTinas({
                  litros: nLitros, kilos: nKilos, esperado,
                  precioLitro: nLitros > 0 ? costoLeche / nLitros : null,
                });
                if (!t) return null;
                const falta = enQuesos(t.quesos);
                return (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.02em' }}
                         className="cifra">
                      {t.tinas} {t.tinas === 1 ? 'tina' : 'tinas'} → {peso(nKilos, 'kg')}
                    </div>
                    {t.kilosEsperados != null && (
                      <div style={{ marginTop: 6 }}>
                        Debían salir <strong>{peso(t.kilosEsperados, 'kg')}</strong>
                        {t.porTina && <> · {producto.nombre} da {promedio(t.porTina)} kg por tina</>}
                      </div>
                    )}
                    {/* La frase que hace levantar la cabeza. Va en quesos y en
                        plata: «rindió 7,87 en vez de 7,02» no mueve a nadie. */}
                    {t.rindioMal && falta && (
                      <div style={{ marginTop: 8, fontWeight: 700 }}>
                        ⚠️ Le faltó {falta} — {peso(Math.abs(t.diferencia), 'kg')} menos.
                        {t.plata > 0 && <> Son <strong>{plata(t.plata)}</strong> de leche
                          que se fueron en agua ({peso(t.litrosDeMas, 'L')}).</>}
                      </div>
                    )}
                    {!t.rindioMal && t.kilosEsperados != null && (
                      <div style={{ marginTop: 8, fontWeight: 600 }}>
                        ✅ Rindió como debía.
                      </div>
                    )}
                    {/* De quién era esta leche. Es lo que él hace de cabeza
                        cuando el total no da: pensar qué fincas entraron ese
                        día. Aquí sale sin buscarlo. */}
                    {/* Si es una prueba, la respuesta no se deduce: se sabe. */}
                    {t.rindioMal && fincaSola && (
                      <div style={{ marginTop: 10, paddingTop: 8,
                                    borderTop: '1px solid rgba(0,0,0,.12)' }}>
                        <div style={{ fontWeight: 700 }}>
                          🔬 Esta es leche de {(fincas.find(f => String(f.id) === String(fincaSola)) || {}).nombre},
                          y de nadie más.
                        </div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                          No hay que deducir de quién era: se aisló para medirla.
                        </div>
                      </div>
                    )}
                    {t.rindioMal && !fincaSola && fincasDelDia(recibos, dia).length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 8,
                                    borderTop: '1px solid rgba(0,0,0,.12)' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          ¿De quién era esta leche?
                        </div>
                        {fincasDelDia(recibos, dia).map(f => (
                          <div key={f.finca_id} style={{ display: 'flex', gap: 12,
                                                         fontSize: 14, padding: '2px 0' }}>
                            <span style={{ minWidth: 170 }}>{f.finca}</span>
                            <span className="cifra">{peso(f.litros, 'L')}</span>
                            <span style={{ color: 'var(--tinta-tenue)' }}>{f.parte}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

              <div style={{ marginTop: 8, fontSize: 13, opacity: .8 }} className="cifra">
                {rendimiento.toFixed(2)} L/kg
                {esperado ? <> · lo normal es {esperado} L/kg</> : null}
                {costoKilo ? <> · {plata(costoKilo)} el kilo</> : null}
              </div>

              {!precioDia && (
                <div style={{ marginTop: 8 }}>
                  No hay entregas de leche anotadas el {fecha(dia)}, así que la cuajada
                  se guardará sin costo de leche.
                </div>
              )}
              {precioDia && (
                <div style={{ marginTop: 6, fontSize: 13, opacity: .85 }}>
                  Leche a {plata(pVaca)}/L
                  {pBufala && pBufala !== pVaca && <> · búfala a {plata(pBufala)}/L</>}
                  , el promedio de lo recibido ese día.
                </div>
              )}
            </div>
          )}

          <div className="acciones">
            <button className="boton" disabled={guardando}>
              {guardando ? 'Guardando…' : '💾 Cerrar la cuajada'}
            </button>
          </div>
        </form>
      </div>

      {/* Los rutas: crearlos y compararlos.
          Va al final de Producción y no en su propia pestaña del menú: quien
          tiene un solo centro no tiene por qué encontrarse una entrada más en
          el menú por una función que no usa. */}
      <div className="bloque">
        <h2>
          🏭 Rutas
          <span className="contador">
            {centros.length === 0 ? 'ninguno' :
             centros.length === 1 ? '1 ruta' : `${centros.length} rutas`}
          </span>
        </h2>
        <div className="cuerpo">
          {centros.length === 0 && (
            <div className="aviso">
              Si hace el queso en más de un sitio, agréguelos aquí. El programa
              va a preguntar en cuál se hizo cada cuajada y va a poder decirle
              cuál rinde mejor. <strong>Con un solo sitio no hace falta.</strong>
            </div>
          )}

          {centros.length > 0 && porCentro && (() => {
            const lista = porCentro.centros || [];
            const sin = porCentro.sin_centro || {};

            // EL MEJOR NO ES EL QUE SACA MÁS KILOS POR TINA.
            //
            // Lo era hasta ahora, y con búfala de por medio eso premia al que
            // tuvo suerte con el reparto de la leche: la búfala rinde bastante
            // más, así que la ruta al que le llegó más saca más kilos por
            // tina sin haber hecho nada mejor.
            //
            // El trofeo va al que más CUMPLE: kilos reales contra los que la
            // leche que entró debía dar. Eso no depende de la mezcla.
            // Solo las que produjeron: este bloque es el resumen del día,
            // no el cuadro de mando. Ese está en la pestaña Rutas.
            const ordenados = compararRutas(lista).filter(x => x.lectura);
            const mejor = ordenados.find(c => c.lectura.cumple !== null) || ordenados[0];
            const hayBufala = lista.some(c => (c.litros_bufala || 0) > 0);
            return (
              <>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[['semana', 'Esta semana'], ['mes', 'Este mes'], ['todo', 'Todo']].map(([id, txt]) => (
                    <button key={id} type="button"
                            className={'boton chico ' + (periodo === id ? '' : 'suave')}
                            onClick={() => setPeriodo(id)}>
                      {txt}
                    </button>
                  ))}
                </div>
                <div className="tabla-caja">
                  <table className="datos">
                    <thead>
                      <tr>
                        <th>Centro</th>
                        <th style={{ textAlign: 'right' }}>Cuajadas</th>
                        <th style={{ textAlign: 'right' }}>Leche</th>
                        <th style={{ textAlign: 'right' }}>Queso</th>
                        <th style={{ textAlign: 'right' }}>Por tina</th>
                        {hayBufala && <th style={{ textAlign: 'right' }}>Búfala</th>}
                        <th style={{ textAlign: 'right' }}>Cumple</th>
                        <th style={{ textAlign: 'right' }}>Costo del kilo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map(c => {
                        const L = lecturaDeRuta(c);
                        return (
                        <tr key={c.id}>
                          <td className="recio">
                            {mejor && c.id === mejor.id && c.cuajadas > 0 && '🏆 '}{c.nombre}
                          </td>
                          <td className="num">
                            {c.cuajadas || '—'}
                            {/* Cuántas quedaron fuera de la comparación. Un
                                porcentaje que tapa la mitad de la producción es
                                peor que no ponerlo. */}
                            {L && L.sinMedir > 0 && (
                              <div style={{ fontSize: 11, fontWeight: 400,
                                            color: 'var(--tinta-tenue)' }}>
                                {L.sinMedir} sin medir
                              </div>
                            )}
                          </td>
                          <td className="num">
                            {c.litros > 0 ? peso(c.litros, 'L') : '—'}
                            {L && (
                              <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                {L.tinas} tinas
                              </div>
                            )}
                          </td>
                          <td className="num recio">{c.kilos > 0 ? peso(c.kilos, 'kg') : '—'}</td>
                          {/* Lo que pidió el cliente: kilos de queso por tina de
                              leche. Es lo que se habla. */}
                          <td className="num recio">{L ? `${promedio(L.porTina)} kg` : '—'}</td>
                          {hayBufala && (
                            <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                              {L && L.parteBufala > 0 ? `${L.parteBufala}%` : '—'}
                            </td>
                          )}
                          {/* Y el que de verdad compara, porque no depende de
                              la mezcla que le tocó a cada ruta. */}
                          <td className="num recio"
                              style={{ color: !L || L.cumple === null ? 'var(--tinta-tenue)'
                                     : L.cumple >= 98 ? 'var(--bueno)'
                                     : L.cumple >= 95 ? 'var(--ojo)' : 'var(--malo)' }}>
                            {L && L.cumple !== null ? `${L.cumple}%` : '—'}
                          </td>
                          <td className="num">{c.costo_kilo ? plata(c.costo_kilo) : '—'}</td>
                        </tr>
                        );
                      })}
                      {/* Lo que no tiene centro se muestra. Esconderlo haría que
                          los totales por ruta no cuadren con la producción, y
                          ese descuadre es el que hace desconfiar. */}
                      {sin.cuajadas > 0 && (
                        <tr>
                          <td style={{ color: 'var(--tinta-tenue)' }}>Sin ruta anotada</td>
                          <td className="num">{sin.cuajadas}</td>
                          <td className="num">{peso(sin.litros, 'L')}</td>
                          <td className="num">{peso(sin.kilos, 'kg')}</td>
                          <td className="num">—</td>
                          {hayBufala && <td className="num">—</td>}
                          <td className="num">—</td>
                          <td className="num">—</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Por qué el trofeo no va al de más kilos por tina. Sin
                    decirlo, la tabla parece contradecirse: una ruta con el
                    número más alto y sin corona. */}
                {hayBufala && (
                  <p style={{ fontSize: 13, color: 'var(--tinta-tenue)',
                              margin: '10px 0 0' }}>
                    <strong>Por tina</strong> es lo que sacó de cada tina.{' '}
                    <strong>Cumple</strong> es lo que sacó contra lo que esa leche
                    debía dar. El 🏆 va por Cumple, no por tina: la búfala rinde
                    más, así que la ruta al que le llega más búfala saca más
                    kilos por tina sin haber hecho nada mejor.
                  </p>
                )}

                {/* Lo que gasta cada ruta de cuajo y de sal, POR CADA 100
                    LITROS. En crudo no diría nada: el que procesa el doble
                    gasta el doble y eso es lo normal. */}
                {gastoInsumos.length > 0 && (() => {
                  const litrosDe = (id) => {
                    const c = lista.find(x => x.id === id);
                    return c ? c.litros : 0;
                  };
                  const nombres = [...new Set(gastoInsumos.map(g => g.insumo))];
                  const conCuajadas = lista.filter(c => c.cuajadas > 0);
                  if (conCuajadas.length < 2) return null;
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        Gasto por cada 100 litros
                      </div>
                      <div className="tabla-caja">
                        <table className="datos">
                          <thead>
                            <tr>
                              <th>Insumo</th>
                              {conCuajadas.map(c => (
                                <th key={c.id} style={{ textAlign: 'right' }}>{c.nombre}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {nombres.map(nombre => {
                              const fila = conCuajadas.map(c => {
                                const g = gastoInsumos.find(
                                  x => x.centro_id === c.id && x.insumo === nombre);
                                const L = litrosDe(c.id);
                                return (g && L > 0)
                                  ? { v: g.usado / L * 100, unidad: g.unidad } : null;
                              });
                              // El que más gasta se marca, pero solo si hay con
                              // qué comparar: con un solo dato no hay «más».
                              const vals = fila.filter(Boolean).map(x => x.v);
                              const peor = vals.length > 1 ? Math.max(...vals) : null;
                              return (
                                <tr key={nombre}>
                                  <td className="recio">{nombre}</td>
                                  {fila.map((x, k) => (
                                    <td key={k} className="num"
                                        style={{ color: (peor && x && x.v === peor)
                                                 ? 'var(--ojo)' : undefined,
                                                 fontWeight: (peor && x && x.v === peor) ? 700 : 400 }}>
                                      {x ? `${x.v.toFixed(x.v < 10 ? 2 : 1)} ${x.unidad}` : '—'}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 6 }}>
                        Si una ruta gasta más cuajo por litro <strong>y además rinde
                        peor</strong>, el problema está en cómo se hace el queso — no
                        en la leche que le llega.
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}

          <form className="campos" style={{ marginTop: 12 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!centroNuevo.trim()) return;
                  try {
                    await axios.post('/api/centros', { nombre: centroNuevo.trim() });
                    setCentroNuevo('');
                    cargar();
                  } catch (err) {
                    alert(err.response?.data?.error || 'No se pudo guardar');
                  }
                }}>
            <label className="campo">
              <span>Agregar una ruta</span>
              <input value={centroNuevo} placeholder="El de arriba, La planta…"
                     onChange={e => setCentroNuevo(e.target.value)} />
            </label>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="boton chico">➕ Agregar</button>
            </div>
          </form>
        </div>
      </div>

      {(() => {
        // El historial. `esperadoDe` sale de la variedad de cada cuajada: el
        // rendimiento normal vive ahí, no en esta pantalla.
        const normalDe = (c) => {
          const p = productos.find(x => x.id === c.producto_id);
          return p ? p.rendimiento_esperado : null;
        };
        const lista = sospechosos(cuajadas, recibos, normalDe)
          .filter(f => f.dias >= 2);
        if (lista.length < 2) return null;   // con una sola finca no hay nada que comparar
        const hayAlguno = lista.some(esSospechoso);
        return (
          <div className="bloque">
            <h2>
              🔍 Quién viene fallando
              <span className="contador">
                {hayAlguno ? 'hay una finca señalada' : 'nadie destaca'}
              </span>
            </h2>
            <div className="cuerpo">
              <div className="aviso">
                Se compara cuánto rinden las cuajadas de los días que entrega
                cada finca contra las de los días que no entrega. <strong>Esto no
                acusa a nadie</strong>: ordena por dónde vale la pena ir a medir.
              </div>
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Finca</th>
                      <th style={{ textAlign: 'right' }}>Días</th>
                      <th style={{ textAlign: 'right' }}>Malos</th>
                      <th style={{ textAlign: 'right' }}>Leche</th>
                      <th>Medido aislando su leche</th>
                      <th>Cuando ella entrega</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map(f => (
                      <tr key={f.finca_id}
                          style={{ background: esSospechoso(f) ? 'var(--hondo)' : undefined }}>
                        <td className="recio">
                          {esSospechoso(f) && '⚠️ '}{f.finca}
                        </td>
                        <td className="num">{f.dias}</td>
                        <td className="num">{f.malos} · {f.porcentajeMalos}%</td>
                        <td className="num">{peso(f.litros, 'L')}</td>
                        {/* Lo medido va ANTES de lo deducido, y en negrita.
                            Una prueba no se discute; que aparezca en días malos
                            es apenas una sospecha. */}
                        <td>
                          {f.pruebas > 0 ? (
                            <span style={{ fontWeight: 700,
                                           color: f.desvioPrueba > 5 ? 'var(--malo)' : 'var(--bueno)' }}>
                              {f.pruebas} {f.pruebas === 1 ? 'prueba' : 'pruebas'} ·{' '}
                              {f.desvioPrueba > 0
                                ? `rinde ${f.desvioPrueba}% peor`
                                : `rinde ${Math.abs(f.desvioPrueba)}% mejor`}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                              sin medir
                            </span>
                          )}
                        </td>
                        <td>
                          {/* Lo que NO se puede saber se dice, en vez de
                              rellenarlo con un número que parecería un juicio. */}
                          {!f.comparable ? (
                            <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                              entrega todos los días · no hay con qué comparar
                            </span>
                          ) : f.contraste > 0 ? (
                            <span style={{ color: esSospechoso(f) ? 'var(--malo)' : 'var(--tinta-tenue)',
                                           fontWeight: esSospechoso(f) ? 700 : 400 }}>
                              rinde {f.contraste}% peor
                            </span>
                          ) : (
                            <span style={{ color: 'var(--bueno)' }}>
                              rinde {Math.abs(f.contraste)}% mejor
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hayAlguno && (
                <div className="aviso ojo" style={{ marginBottom: 0 }}>
                  Antes de hablar con el dueño, <strong>mídala</strong>: cuaje su
                  leche sola y marque la finca en «¿Leche de una sola finca?».
                  Eso deja de ser una sospecha y pasa a ser un dato, y es lo
                  único que aguanta una conversación con el dueño de la finca.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="bloque">
        <h2>
          📦 Lotes producidos
          <span className="contador">{cuajadas.length}</span>
        </h2>
        {cuajadas.length === 0 ? (
          <div className="vacio">Todavía no se ha cerrado ninguna cuajada.</div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Fecha</th>
                  <th>Variedad</th>
                  <th style={{ textAlign: 'right' }}>Leche</th>
                  <th style={{ textAlign: 'right' }}>Queso</th>
                  <th style={{ textAlign: 'right' }}>Rinde</th>
                  <th style={{ textAlign: 'right' }}>Costo/kg</th>
                  <th>Vence</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cuajadas.map(c => {
                  const mal = c.rendimiento_esperado &&
                              c.rendimiento > c.rendimiento_esperado * 1.05;
                  return (
                    <tr key={c.id}>
                      <td className="recio cifra">{c.lote}</td>
                      <td>{fecha(c.fecha)}</td>
                      <td>{c.producto}</td>
                      <td className="num">{peso(c.litros, 'L')}</td>
                      <td className="num">{peso(c.kilos, 'kg')}</td>
                      <td className="num">
                        <span className={'pinta ' + (mal ? 'ojo' : 'bueno')}>
                          {c.rendimiento} L/kg
                        </span>
                      </td>
                      <td className="num recio">{c.costo_kilo ? plata(c.costo_kilo) : '—'}</td>
                      <td>{fecha(c.vence)}</td>
                      <td>
                        <button className="boton chico suave" onClick={() => borrar(c)} title="Borrar">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Produccion;
